import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AlertTriangle, Search, X } from "lucide-react";
import { useApp, getEffectiveMatches } from "@/store/app";
import type { Match } from "@/data/matches";
import { MatchCard } from "@/components/MatchCard";
import { TeamFlag } from "@/components/TeamFlag";
import { useT, useLang, useTCountry } from "@/i18n";
import { TEAM_SHORT_CODES } from "@/lib/flags";
import logo from "@/assets/logo.png";

export const Route = createFileRoute("/_app/predictions")({
  component: PredictionsPage,
});

type Filter = "unpredicted" | "finished" | "all";

function PredictionsPage() {
  const t = useT();
  const lang = useLang((s) => s.lang);
  const tc = useTCountry();
  const overrides = useApp((s) => s.matchOverrides);
  const predictions = useApp((s) => s.predictions);
  const savePrediction = useApp((s) => s.savePrediction);

  const matches = getEffectiveMatches(overrides);

  const liveMatches = matches.filter((m) => m.status === "live");
  const upcomingMatches = matches.filter((m) => m.status === "upcoming");

  // Demo: seed predictions for live matches so users can see how predictions look
  useEffect(() => {
    const demoSeeds: Record<number, { homeScore: number; awayScore: number; firstScorerTeam: "H" | "A"; firstScorer: string }> = {
      4: { homeScore: 2, awayScore: 1, firstScorerTeam: "H", firstScorer: "Musiala" },
      5: { homeScore: 3, awayScore: 0, firstScorerTeam: "H", firstScorer: "Neymar" },
    };
    for (const m of liveMatches) {
      if (!predictions[m.id] && demoSeeds[m.id]) {
        savePrediction(m.id, demoSeeds[m.id]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveMatches.length]);

  const [filter, setFilter] = useState<Filter>(
    liveMatches.length > 0 || upcomingMatches.length > 0 ? "unpredicted" : "all"
  );
  const [search, setSearch] = useState("");
  const showSearch = filter === "finished" || filter === "all";

  function handleFilterChange(f: Filter) {
    setFilter(f);
    if (f === "unpredicted") setSearch("");
  }

  const filters: { id: Filter; label: string; count: number }[] = [
    {
      id: "unpredicted",
      label: t("Upcoming & Live", "Próximas & Ao vivo"),
      count: liveMatches.length + upcomingMatches.length,
    },
    {
      id: "finished",
      label: t("Finished", "Finalizadas", "Încheiate"),
      count: matches.filter((m) => m.status === "finished").length,
    },
    { id: "all", label: t("All", "Todas", "Toate"), count: matches.length },
  ];

  const filtered =
    filter === "unpredicted"
      ? upcomingMatches
      : filter === "finished"
        ? matches.filter((m) => m.status === "finished")
        : matches;

  const searchQuery = search.trim().toLowerCase();
  const displayMatches = searchQuery
    ? filtered.filter((m) => {
        const homeCode = (TEAM_SHORT_CODES[m.homeTeam] ?? m.homeTeam.slice(0, 3)).toLowerCase();
        const awayCode = (TEAM_SHORT_CODES[m.awayTeam] ?? m.awayTeam.slice(0, 3)).toLowerCase();
        return (
          m.homeTeam.toLowerCase().includes(searchQuery) ||
          m.awayTeam.toLowerCase().includes(searchQuery) ||
          homeCode.includes(searchQuery) ||
          awayCode.includes(searchQuery) ||
          tc(m.homeTeam).toLowerCase().includes(searchQuery) ||
          tc(m.awayTeam).toLowerCase().includes(searchQuery)
        );
      })
    : filtered;

  const showLiveSection = filter === "unpredicted" && liveMatches.length > 0;

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold tracking-tight">{t("Predictions", "Pronosticuri")}</h1>
      <div className="mb-3 flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">

        {filters.map((f) => (
          <button
            key={f.id}
            onClick={() => handleFilterChange(f.id)}
            className={`whitespace-nowrap rounded-full border h-9 px-4 py-2 text-sm font-semibold transition-colors md:h-auto md:px-2.5 md:py-1 md:text-xs md:font-medium ${
              filter === f.id
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border/60 bg-card text-muted-foreground hover:bg-accent hover:text-foreground"
            }`}
          >
            {f.label} <span className="opacity-70">({f.count})</span>
          </button>
        ))}
      </div>

      {showSearch && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Search by team or code…", undefined, "Caută după echipă sau cod…")}
            className="w-full rounded-xl border bg-card py-2.5 pl-9 pr-9 text-base outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {showLiveSection && (
        <section className="mb-8">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex items-baseline gap-2">
              <h2 className="text-lg font-bold tracking-tight text-live">
                {t("Live now", "Ao vivo")}
              </h2>
              <span className="text-xs font-medium text-muted-foreground">
                {liveMatches.length} {t(liveMatches.length === 1 ? "match" : "matches", "partida(s)")}
              </span>
            </div>
            <div className="h-px flex-1 bg-border" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {liveMatches.map((m) => (
              <MatchCard key={m.id} match={m} />
            ))}
          </div>
        </section>
      )}

      {displayMatches.length === 0 ? (
        !showLiveSection && (
          <div className="rounded-2xl border border-dashed py-16 text-center text-muted-foreground">
            <img src={logo} alt="" className="mx-auto h-16 w-16 object-contain opacity-80" />
            <p className="mt-3">
              {searchQuery
                ? t("No matches found.", undefined, "Niciun meci găsit.")
                : t("No matches in this filter.", "Nenhuma partida neste filtro.")}
            </p>
          </div>
        )
      ) : (
        <div className="space-y-8">
          {groupByDay(displayMatches, t, filter !== "unpredicted").map((group) => (
            <section key={group.key}>
              <div className="mb-3 flex items-center gap-3">
                <h2 className="text-lg font-bold tracking-tight">
                  {group.label}
                </h2>
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs font-medium text-muted-foreground">
                  {group.matches.length}{" "}
                  {t(
                    group.matches.length === 1 ? "match" : "matches",
                    group.matches.length === 1 ? "partida" : "partidas"
                  )}
                </span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {group.matches.map((m) => {
                  const missed =
                    filter !== "unpredicted" &&
                    m.status === "finished" &&
                    !predictions[m.id];
                  return missed ? (
                    <MissedMatchCard key={m.id} match={m} lang={lang} t={t} />
                  ) : (
                    <MatchCard key={m.id} match={m} />
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

const WEEKDAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function groupByDay(
  matches: ReturnType<typeof getEffectiveMatches>,
  t: (en: string) => string,
  descending = false
) {
  const map = new Map<string, typeof matches>();
  for (const m of matches) {
    const d = new Date(m.date);
    const key = d.toLocaleDateString("en-CA", { timeZone: "Europe/Bucharest" });
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(m);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => descending ? b.localeCompare(a) : a.localeCompare(b))
    .map(([key, ms]) => {
      // key is "YYYY-MM-DD" — derive weekday/day/month from a Bucharest-local midday.
      const ref = new Date(key + "T12:00:00Z");
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "Europe/Bucharest",
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      }).formatToParts(ref);
      const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
      const month = parts.find((p) => p.type === "month")?.value ?? "";
      const day = parts.find((p) => p.type === "day")?.value ?? "";
      const label = `${t(weekday)}, ${day} ${t(month)}`;
      return {
        key,
        label,
        matches: ms.sort(
          (a, b) => descending
            ? new Date(b.date).getTime() - new Date(a.date).getTime()
            : new Date(a.date).getTime() - new Date(b.date).getTime()
        ),
      };
    });
}

function MissedMatchCard({
  match,
  lang,
  t,
}: {
  match: Match;
  lang: "en" | "ro";
  t: (en: string, pt?: string, ro?: string) => string;
}) {
  const homeCode = TEAM_SHORT_CODES[match.homeTeam] ?? match.homeTeam.slice(0, 3).toUpperCase();
  const awayCode = TEAM_SHORT_CODES[match.awayTeam] ?? match.awayTeam.slice(0, 3).toUpperCase();
  const home = match.result?.homeScore ?? 0;
  const away = match.result?.awayScore ?? 0;
  const hps = match.result?.homePenScore;
  const aps = match.result?.awayPenScore;
  const pts = lang === "ro" ? "pct" : "pts";
  return (
    <div className="rounded-2xl border border-l-2 border-l-amber-500 bg-card/60 p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="flex flex-1 flex-wrap items-center gap-2 text-base font-bold tabular-nums">
          <TeamFlag team={match.homeTeam} className="h-4 w-6 rounded-sm shadow-sm" />
          <span>{homeCode}</span>
          <span className="px-1 text-foreground/80">{home}</span>
          <span className="text-muted-foreground">–</span>
          <span className="px-1 text-foreground/80">{away}</span>
          <span>{awayCode}</span>
          <TeamFlag team={match.awayTeam} className="h-4 w-6 rounded-sm shadow-sm" />
          {hps != null && aps != null && (
            <span className="text-xs font-semibold text-muted-foreground">
              (pen. {hps}–{aps})
            </span>
          )}
        </div>
        <span className="shrink-0 text-sm font-bold tabular-nums text-muted-foreground">
          0 {pts}
        </span>
      </div>
      <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-500">
        <AlertTriangle className="h-3.5 w-3.5" />
        {t("No prediction", undefined, "Fără pronostic")}
      </div>
    </div>
  );
}
